import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import { RiskAssessmentResultControlType as Risk_Assessment_Result_Control_Type_Enum } from '@risksmart-app/domain/src/types/consts/risk-assessment-result-control-type';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAggregation } from 'src/hooks/useAggregation';

import { useConfigIds } from '@/ratings/useConfigIds';
import { useRiskRatingResolver } from '@/ratings/useRiskRatingResolver';

import { useControlTypeLabel } from '../forms/useControlTypeLabel';
import { decorateWithControlType, getParentTitle } from './helpers';
import type {
  AssessmentResultFields,
  AssessmentResultRegisterFields,
} from './types';

const getConfigId = (d: AssessmentResultFields): string | undefined =>
  d.ResultType === 'risk_assessment_result'
    ? (d.ConfigId ?? undefined)
    : undefined;

export const useLabelledFields = (
  records: AssessmentResultFields[] | undefined
) => {
  const { t: at } = useTranslation('common', {
    keyPrefix: 'assessmentResults',
  });
  const { getLabel: getStatusLabel } = useRating('assessment_status');
  const assessmentResultTypes = at('resultTypes', { returnObjects: true });
  const getControlTypeLabel = useControlTypeLabel();
  const { riskModel } = useAggregation();
  const { getByValue: getByResultValue } = useRating('performance_result');
  const { getByValue: getEffectivenessByValue } = useRating('effectiveness');
  const configIds = useConfigIds(records);
  const { resolveRiskRating, resolveImpact, resolveLikelihood } =
    useRiskRatingResolver('standard', {
      configType: 'risk_assessment',
      configIds,
    });

  return useMemo<AssessmentResultRegisterFields[] | undefined>(() => {
    const ratingFns = {
      document_assessment_result: (d: AssessmentResultFields) =>
        getByResultValue(d.Rating),
      obligation_assessment_result: (d: AssessmentResultFields) =>
        getByResultValue(d.Rating),
      test_result: (d: AssessmentResultFields) =>
        getEffectivenessByValue(d.OverallEffectiveness),
      risk_assessment_result: (d: AssessmentResultFields) => {
        const controlType = d.ControlType;
        if (
          controlType == null ||
          riskModel === 'control_effectiveness_averages'
        ) {
          return;
        }

        return resolveRiskRating({
          likelihood: d.Likelihood,
          impact: d.Impact,
          controlType:
            controlType === 'Uncontrolled'
              ? Risk_Assessment_Result_Control_Type_Enum.Uncontrolled
              : Risk_Assessment_Result_Control_Type_Enum.Controlled,
          rating: d.Rating,
          configId: getConfigId(d),
        });
      },
    };

    const likelihoodFns = {
      document_assessment_result: (_d: AssessmentResultFields) => null,
      obligation_assessment_result: (_d: AssessmentResultFields) => null,
      test_result: (_d: AssessmentResultFields) => null,
      risk_assessment_result: (d: AssessmentResultFields) =>
        resolveLikelihood(d.Likelihood, getConfigId(d)),
    };

    const impactFns = {
      document_assessment_result: (_d: AssessmentResultFields) => null,
      obligation_assessment_result: (_d: AssessmentResultFields) => null,
      test_result: (_d: AssessmentResultFields) => null,
      risk_assessment_result: (d: AssessmentResultFields) =>
        resolveImpact(d.Impact, getConfigId(d)),
    };

    return records?.map((d) => {
      const assessmentStatus = d.assessments.find((a) => a.assessment)
        ?.assessment?.Status;

      return {
        ...d,
        AssessmentTitle:
          d.assessments.find((a) => a.assessment)?.assessment?.Title || '-',
        TypeLabelled: decorateWithControlType(
          assessmentResultTypes[d.ResultType],
          getControlTypeLabel,
          d
        ),
        ParentTitle: getParentTitle(d) || '-',
        RatingLabelled: ratingFns[d.ResultType](d)?.label || '-',
        ImpactLabelled: impactFns[d.ResultType](d)?.label || '-',
        LikelihoodLabelled: likelihoodFns[d.ResultType](d)?.label || '-',
        StartDate: d.assessments.find((a) => a.assessment)?.assessment
          ?.StartDate,
        ActualCompletionDate: d.assessments.find((a) => a.assessment)
          ?.assessment?.ActualCompletionDate,
        CompletedByUser:
          d.assessments.find((a) => a.assessment)?.assessment?.completedByUser
            ?.FriendlyName || '-',
        Rationale: d.Rationale,
        TestDate: d.TestDate,
        Status: assessmentStatus ?? '-',
        StatusLabelled: assessmentStatus
          ? getStatusLabel(assessmentStatus)
          : '-',
        originalResult: d,
      };
    });
  }, [
    records,
    getByResultValue,
    getEffectivenessByValue,
    riskModel,
    resolveRiskRating,
    resolveLikelihood,
    resolveImpact,
    assessmentResultTypes,
    getControlTypeLabel,
    getStatusLabel,
  ]);
};
